import React from "react";
import { useTranslation } from "react-i18next";
import { Cart, ItemRef, PricingData } from "../../types/PricingCalculator";
import {
  needsPrinter,
  compatiblePrinters,
  requiredAdapters,
  formatCurrency,
  convert,
} from "../../utils/pricingLogic";

interface HardwareStepProps {
  cart: Cart;
  pricingData: PricingData;
  onUpdate: (updates: Partial<Cart>) => void;
}

const HardwareStep: React.FC<HardwareStepProps> = ({
  cart,
  pricingData,
  onUpdate,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as string;

  const pickDescription = (base: {
    description: string;
    description_fr?: string;
    description_rw?: string;
  }) => {
    if (lang.startsWith("fr") && base.description_fr)
      return base.description_fr;
    if ((lang === "rw" || lang.startsWith("rw")) && base.description_rw)
      return base.description_rw;
    return base.description;
  };
  const handleSelection = (category: string, item: ItemRef) => {
    const updates: Partial<Cart> = {
      selections: {
        ...cart.selections,
        [category]: item,
      },
    };

    // If selecting a screen with built-in printer, auto-satisfy printer requirement
    if (category === "screen" && item.meta?.builtInPrinter) {
      updates.selections!.printer = item;
    }

    // Calculate required adapters
    if (cart.strategy.existingSmartphone) {
      const newSelections = { ...cart.selections, [category]: item };
      const adapters = requiredAdapters(cart.strategy, {
        printer: newSelections.printer,
        scanner: newSelections.scanner,
      });
      updates.selections!.adapters = adapters;
    }

    onUpdate(updates);
  };

  const handleRemoveSelection = (category: string) => {
    const updates: Partial<Cart> = {
      selections: {
        ...cart.selections,
        [category]: undefined,
      },
    };

    // If removing screen with built-in printer, also remove printer
    if (category === "screen" && cart.selections.screen?.meta?.builtInPrinter) {
      updates.selections!.printer = undefined;
    }

    // Recalculate adapters
    if (cart.strategy.existingSmartphone) {
      const newSelections = { ...cart.selections, [category]: undefined };
      const adapters = requiredAdapters(cart.strategy, {
        printer: newSelections.printer,
        scanner: newSelections.scanner,
      });
      updates.selections!.adapters = adapters;
    }

    onUpdate(updates);
  };

  const renderItemCard = (
    item: ItemRef,
    category: string,
    isSelected: boolean
  ) => (
    <div
      key={item.id}
      className={`border-2 rounded-xl cursor-pointer transition-all duration-200 relative group overflow-hidden ${
        isSelected
          ? "border-blue-500 bg-blue-50 shadow-lg"
          : "border-gray-200 hover:border-gray-300 hover:shadow-md bg-white"
      }`}
      onClick={() => handleSelection(category, item)}
    >
      {/* Image Section */}
      {item.image && (
        <div className="relative">
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-48 object-cover"
          />
          {/* Hover Image Preview */}
          <div className="absolute top-0 left-full ml-4 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4 w-96">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-80 object-cover rounded-lg"
              />
              <div className="mt-4 text-center">
                <p className="text-lg font-medium text-gray-900">{item.name}</p>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
              {item.name}
            </h3>
            <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
              {item.description}
            </p>
          </div>
          {isSelected && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveSelection(category);
              }}
              className="ml-3 text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Price and Badges */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xl font-bold text-blue-600">
              {formatCurrency(convert(item.price, cart.fx, "RWF"), "RWF")}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span
              className={`px-2 py-1 text-xs rounded-full font-medium ${
                item.availability === "Rwanda"
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {item.availability}
            </span>
            {item.meta?.builtInPrinter && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                ✓ Built-in Printer
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const allInOneDevices = pricingData.all_in_one_devices.map((device) => ({
    ...device,
    id: `all-in-one-${device.name.toLowerCase().replace(/\s+/g, "-")}`,
    category: "screen" as const,
    price: { value: device.price, currency: device.currency },
    description: pickDescription(device),
    meta: { builtInPrinter: true },
  }));

  const smartphones = pricingData.phone_and_printer_solution.smartphones.map(
    (phone) => ({
      ...phone,
      id: `smartphone-${phone.name.toLowerCase().replace(/\s+/g, "-")}`,
      category: "screen" as const,
      price: { value: phone.price, currency: phone.currency },
      description: pickDescription(phone),
    })
  );

  const printers = pricingData.phone_and_printer_solution.printers.map(
    (printer) => ({
      ...printer,
      id: `printer-${printer.name.toLowerCase().replace(/\s+/g, "-")}`,
      category: "printer" as const,
      price: { value: printer.price, currency: printer.currency },
      description: pickDescription(printer),
      meta: { bt: true, usb: true },
    })
  );

  const scanners = pricingData.optional_accessories.barcode_scanners.map(
    (scanner) => ({
      ...scanner,
      id: `scanner-${scanner.name.toLowerCase().replace(/\s+/g, "-")}`,
      category: "scanner" as const,
      price: { value: scanner.price, currency: scanner.currency },
      description: pickDescription(scanner),
      meta: {
        bt: scanner.name.toLowerCase().includes("bluetooth"),
        usb: scanner.name.toLowerCase().includes("wired"),
      },
    })
  );

  const compatiblePrinterList = cart.strategy.existingSmartphone
    ? compatiblePrinters(cart.strategy, printers)
    : printers;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-300">
          {t("pricingCalc.hardware.heading")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">{t("pricingCalc.hardware.subheading")}</p>
      </div>

      {/* Screen Selection */}
      {!cart.strategy.existingSmartphone && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-300">
            {t("pricingCalc.hardware.screenHeading")}
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="text-md font-medium text-gray-700 mb-3 dark:text-gray-400">
                {t("pricingCalc.hardware.allInOne")}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allInOneDevices.map((device) =>
                  renderItemCard(
                    device,
                    "screen",
                    cart.selections.screen?.id === device.id
                  )
                )}
              </div>
            </div>

            <div>
              <h4 className="text-md font-medium text-gray-700 mb-3 dark:text-gray-400">
                {t("pricingCalc.hardware.smartphones")}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {smartphones.map((phone) =>
                  renderItemCard(
                    phone,
                    "screen",
                    cart.selections.screen?.id === phone.id
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Printer Selection */}
      {needsPrinter(cart.strategy, cart.selections.screen) && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-400">
            {t("pricingCalc.hardware.printerHeading")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {compatiblePrinterList.map((printer) =>
              renderItemCard(
                printer,
                "printer",
                cart.selections.printer?.id === printer.id
              )
            )}
          </div>
        </div>
      )}

      {/* Scanner Selection (Optional) */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-400">
          {t("pricingCalc.hardware.scannerHeading")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {scanners.map((scanner) =>
            renderItemCard(
              scanner,
              "scanner",
              cart.selections.scanner?.id === scanner.id
            )
          )}
        </div>
      </div>

      {/* Required Adapters */}
      {cart.selections.adapters && cart.selections.adapters.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t("pricingCalc.hardware.adaptersHeading")}
          </h3>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.726-1.36 3.491 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">
                  {t("pricingCalc.hardware.adaptersTitle")}
                </h4>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{t("pricingCalc.hardware.adaptersInfo")}</p>
                  <ul className="list-disc list-inside mt-1">
                    {cart.selections.adapters.map((adapter) => (
                      <li key={adapter.id}>
                        {adapter.name} -{" "}
                        {formatCurrency(
                          convert(adapter.price, cart.fx, "RWF"),
                          "RWF"
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selection Summary */}
      {(cart.selections.screen ||
        cart.selections.printer ||
        cart.selections.scanner) && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            {t("pricingCalc.hardware.selection")}
          </h3>
          <div className="space-y-2">
            {cart.selections.screen && (
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-900">
                  {t("pricingCalc.hardware.labelScreen")}
                </span>
                <span className="font-medium dark:text-gray-600">
                  {cart.selections.screen.name}
                </span>
              </div>
            )}
            {cart.selections.printer && (
              <div className="flex justify-between">
                <span className="text-gray-700">
                  {t("pricingCalc.hardware.labelPrinter")}
                </span>
                <span className="font-medium dark:text-gray-600">
                  {cart.selections.printer.name}
                </span>
              </div>
            )}
            {cart.selections.scanner && (
              <div className="flex justify-between">
                <span className="text-gray-700">
                  {t("pricingCalc.hardware.labelScanner")}
                </span>
                <span className="font-medium">
                  {cart.selections.scanner.name}
                </span>
              </div>
            )}
            {cart.selections.adapters &&
              cart.selections.adapters.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-700">
                    {t("pricingCalc.hardware.labelAdapters")}
                  </span>
                  <span className="font-medium dark:text-gray-600">
                    {cart.selections.adapters.length}{" "}
                    {t("pricingCalc.hardware.itemsCount")}
                  </span>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HardwareStep;
